'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useMemo, useRef, useState, useTransition } from 'react'
import dynamic from 'next/dynamic'
import { Button } from '@/components/ui/button'
import { SuburbSearch } from './SuburbSearch'

const FilterPanel = dynamic(
  () => import('./FilterPanel').then((mod) => ({ default: mod.FilterPanel })),
  {
    ssr: false,
  },
)

interface ListingFiltersProps {
  vertical: 'sale' | 'rent' | 'sold' | 'commercial' | 'leased' | 'commercialSold' | 'commercialLeased'
  /** Hide the Min/Max price filter — commercial lease stock has no prices to filter on. */
  hidePrice?: boolean
  /**
   * Figma's filter row has no map/list toggle in any of the 21 listing frames
   * (4081:48885 / 4081:85950) — the sibling "switch group" instances are hidden.
   * Off by default; opt in only where a design calls for it.
   */
  showViewToggle?: boolean
  /**
   * Number of results rendered on the first page (server-provided).
   * @deprecated Figma shows the total only ("216 Results"), not a range — no longer displayed.
   */
  shownCount?: number
  /** Total number of matching results (server-provided) */
  totalCount?: number
  /**
   * Figma Upcoming Inspections pages (4081:78466 / 4081:86206): the sort trigger
   * reads "Next Inspection" and it is the default order. Adds the option and makes
   * it the fallback when no ?sort param is present.
   */
  inspectionSort?: boolean
}

const FILTER_KEYS = [
  'type',
  'minPrice',
  'maxPrice',
  'bed',
  'bath',
  'car',
  'surroundingSuburbs',
  'radius',
  'offMarket',
  'vacancy',
] as const

export function ListingFilters({
  vertical,
  hidePrice = false,
  showViewToggle = false,
  totalCount,
  inspectionSort = false,
}: ListingFiltersProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isPending, startTransition] = useTransition()
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const searchRef = useRef(search)
  searchRef.current = search
  // ?filters=open (home hero filter button, Asana 005) lands with the panel open
  const [filtersOpen, setFiltersOpen] = useState(() => searchParams.get('filters') === 'open')

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (searchParams.get('type')) count++
    if (searchParams.get('minPrice')) count++
    if (searchParams.get('maxPrice')) count++
    if (searchParams.get('bed')) count++
    if (searchParams.get('bath')) count++
    if (searchParams.get('car')) count++
    if (searchParams.get('surroundingSuburbs') === 'true') count++
    if (searchParams.get('offMarket') === 'true') count++
    return count
  }, [searchParams])

  const currentFilters = useMemo(
    () => ({
      type: searchParams.get('type') || '',
      minPrice: searchParams.get('minPrice') || '',
      maxPrice: searchParams.get('maxPrice') || '',
      bed: searchParams.get('bed') || '',
      bath: searchParams.get('bath') || '',
      car: searchParams.get('car') || '',
      surroundingSuburbs: searchParams.get('surroundingSuburbs') === 'true',
      radius: searchParams.get('radius') || '5',
      offMarket: searchParams.get('offMarket') === 'true',
      vacancy: searchParams.get('vacancy') || '',
    }),
    [searchParams],
  )

  const updateParams = useCallback(
    (key: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString())
      if (value) {
        params.set(key, value)
      } else {
        params.delete(key)
      }
      params.delete('page')
      startTransition(() => {
        router.push(`?${params.toString()}`)
      })
    },
    [router, searchParams],
  )

  const handleSearch = useCallback(() => {
    updateParams('search', searchRef.current)
  }, [updateParams])

  const handleApplyFilters = useCallback(
    (filters: typeof currentFilters) => {
      const params = new URLSearchParams(searchParams.toString())
      params.delete('page')

      // Set or delete each filter param
      if (filters.type) params.set('type', filters.type)
      else params.delete('type')
      if (filters.minPrice) params.set('minPrice', filters.minPrice)
      else params.delete('minPrice')
      if (filters.maxPrice) params.set('maxPrice', filters.maxPrice)
      else params.delete('maxPrice')
      if (filters.bed) params.set('bed', filters.bed)
      else params.delete('bed')
      if (filters.bath) params.set('bath', filters.bath)
      else params.delete('bath')
      if (filters.car) params.set('car', filters.car)
      else params.delete('car')
      if (filters.surroundingSuburbs) {
        params.set('surroundingSuburbs', 'true')
        params.set('radius', filters.radius || '5')
      } else {
        params.delete('surroundingSuburbs')
        params.delete('radius')
      }
      if (filters.offMarket) params.set('offMarket', 'true')
      else params.delete('offMarket')
      if (filters.vacancy) params.set('vacancy', filters.vacancy)
      else params.delete('vacancy')

      startTransition(() => {
        router.push(`?${params.toString()}`)
      })
    },
    [router, searchParams],
  )

  const handleClearFilters = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString())
    params.delete('page')
    for (const key of FILTER_KEYS) params.delete(key)
    startTransition(() => {
      router.push(`?${params.toString()}`)
    })
  }, [router, searchParams])

  // "216 Results" — total only, per Figma text node 4081:57893 / 4081:58888 / 4081:59711
  const resultsText = typeof totalCount === 'number' ? `${totalCount} Results` : null

  return (
    <>
      {/* Filter band — two equal columns (Figma 4081:58878 / 4081:58887): 1472 = 704 + 64 + 704
          at 1600, 704 = 336 + 32 + 336 at 768, single stacked column at 390.
          Band bottom padding: 64 at 1600/768, 32 at 390. */}
      <div
        data-visual="listing-filters"
        className="mb-8 md:mb-16 flex flex-col gap-8 md:flex-row md:items-start lg:gap-16"
      >
        {/* Left column — Trigger over [icon button + Search] at gap/gap-1 (4px); the three
            controls sit on one row from 1024 up (564 + 4 + 36 + 4 + 96 = 704).
            Placeholder copy is the literal string on Figma Trigger 4081:57541,
            "Search by suburb..." — trailing ellipsis included. */}
        <div className="flex flex-col gap-1 md:flex-1 lg:flex-row lg:items-center">
          <SuburbSearch
            value={search}
            onChange={setSearch}
            onSubmit={handleSearch}
            placeholder="Search by suburb..."
            className="w-full min-w-0 lg:flex-1"
          />
          {/* Button pair — icon button 36 + Search 96 at gap/gap-1 (4px), Figma 4081:58884 */}
          <div className="flex items-center gap-1">
            {/* Filters trigger — Figma 4081:130713 "icon button": 36×36, 1px theme/border,
                transparent fill, shadow/xs, radius sm, 16px list-filter glyph. */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setFiltersOpen(true)}
              aria-label="Filters"
              title="Filters"
              className="relative shrink-0"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 6h18" />
                <path d="M7 12h10" />
                <path d="M10 18h4" />
              </svg>
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-foreground text-background rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </Button>
            {/* Search button — Figma 4081:130714 "button" type=primary size=Regular:
                theme/accent fill, h36, px24, radius sm, Body/bodyMdAlt 16/24 regular. */}
            <Button onClick={handleSearch} loading={isPending} className="shrink-0">
              Search
            </Button>
          </div>
        </div>
        {/* Right column — results count + sort Trigger, right-aligned in the column at
            768/1600 (Figma 4081:58887) and reversed to sort-then-count at 390
            (4081:59712 at x=0, count at x=172). Row is 36 tall; the 28-high Trigger centres. */}
        <div className="flex flex-row-reverse items-center justify-end gap-3 md:min-h-9 md:flex-1 md:flex-row">
          {resultsText && (
            <span className="text-body-sm font-light text-muted-foreground whitespace-nowrap">
              {resultsText}
            </span>
          )}
          {/* Sort select — Figma Trigger 4081:57896: 160×28, fill theme/muted #e0dfe0,
              1px theme/border, radius sm (2px), shadow/xs, Body/bodySm light 14/16
              in theme/muted-foreground, chevron in icon/icon-primary. */}
          <div className="relative shrink-0">
            <select
              value={searchParams.get('sort') || (inspectionSort ? 'inspection' : 'newest')}
              onChange={(e) => updateParams('sort', e.target.value)}
              className="appearance-none h-7 w-[160px] bg-muted border border-border rounded-sm shadow-xs pl-3 pr-8 text-body-sm font-light text-muted-foreground focus:outline-none cursor-pointer"
            >
              {inspectionSort && <option value="inspection">Next Inspection</option>}
              <option value="newest">Newest First</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="suburb">Suburb A-Z</option>
            </select>
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-foreground"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </div>
          {showViewToggle && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => updateParams('view', searchParams.get('view') === 'map' ? '' : 'map')}
              className="shrink-0"
              title={searchParams.get('view') === 'map' ? 'List view' : 'Map view'}
            >
              {searchParams.get('view') === 'map' ? (
                <svg
                  className="size-[18px]"
                  width="18"
                  height="18"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.75"
                >
                  <rect x="1" y="1" width="6" height="4" rx="0.5" />
                  <rect x="1" y="7" width="6" height="4" rx="0.5" />
                  <rect x="9" y="1" width="6" height="4" rx="0.5" />
                  <rect x="9" y="7" width="6" height="4" rx="0.5" />
                </svg>
              ) : (
                <svg
                  className="size-[18px]"
                  width="18"
                  height="18"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M8 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
                  <path d="M8 14s-5-4.5-5-8a5 5 0 0 1 10 0c0 3.5-5 8-5 8Z" />
                </svg>
              )}
            </Button>
          )}
        </div>
      </div>

      <FilterPanel
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        initial={currentFilters}
        vertical={vertical}
        hidePrice={hidePrice}
      />
    </>
  )
}
